import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Activity, AlertTriangle, TrendingUp, Info, Loader2, Cpu } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, YAxis, Tooltip as RTooltip } from "recharts";

/**
 * InjuryRiskPanel
 * ───────────────
 * Coach-facing panel that surfaces the predictive risk score for a
 * single player, powered by GET /api/injury-risk/{player_user_id}.
 * Renders:
 *   - Big score + band pill (low/moderate/high/critical)
 *   - Sorted factors list with each factor's contribution
 *   - Ranked recommendations
 *   - Model disclaimer footer
 */
const BAND_COLORS = {
  critical: { bg: "rgba(239,68,68,0.10)",  border: "rgba(239,68,68,0.5)",  fg: "#EF4444" },
  high:     { bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.5)", fg: "#F59E0B" },
  moderate: { bg: "rgba(34,211,238,0.08)", border: "rgba(34,211,238,0.4)", fg: "#22D3EE" },
  low:      { bg: "rgba(16,185,129,0.08)", border: "rgba(16,185,129,0.4)", fg: "#10B981" },
};

const SEVERITY_COLORS = {
  critical: "#EF4444",
  high:     "#F59E0B",
  moderate: "#22D3EE",
  low:      "#94A3B8",
};

export default function InjuryRiskPanel({ playerUserId }) {
  const navigate = useNavigate();
  const [risk, setRisk] = useState(null);
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!playerUserId) return;
    (async () => {
      setRisk(null); setErr(""); setHistory([]);
      try {
        const { data } = await api.get(`/injury-risk/${playerUserId}`);
        setRisk(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not compute risk");
        return;
      }
      try {
        const { data: h } = await api.get(`/injury-risk/${playerUserId}/history?days=30`);
        setHistory(Array.isArray(h?.points) ? h.points : []);
      } catch { /* silent — sparkline just hides */ }
    })();
  }, [playerUserId]);

  const trend = useMemo(() => {
    if (!history.length) return null;
    const first = history[0].score;
    const last = history[history.length - 1].score;
    const delta = last - first;
    return { first, last, delta, direction: delta > 2 ? "up" : delta < -2 ? "down" : "flat" };
  }, [history]);

  const openInStudio = async () => {
    // Ask the router for this athlete's most-diagnostic capture frame,
    // then deep-link into the Studio at that timestamp.
    try {
      const { data } = await api.get(`/motioniq/deep-link/nearest?athlete_user_id=${playerUserId}`);
      if (!data?.ok) {
        // Fall through: land on the Studio home so the coach can seed / upload.
        navigate("/dashboard/motioniq");
        return;
      }
      const q = data.event ? `?event=${data.event}` : "";
      navigate(`/dashboard/motioniq/${data.capture_id}${q}`);
    } catch {
      navigate("/dashboard/motioniq");
    }
  };

  if (err) {
    return <div className="rounded-xl ink-card p-5 text-sm text-red-300" data-testid="injury-risk-error">{String(err)}</div>;
  }
  if (!risk) {
    return (
      <div className="rounded-xl ink-card p-5 flex items-center gap-2 text-sm text-white/55" data-testid="injury-risk-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Computing predictive risk…
      </div>
    );
  }
  const palette = BAND_COLORS[risk.band] || BAND_COLORS.low;

  return (
    <section className="rounded-xl ink-card overflow-hidden" data-testid="injury-risk-panel">
      <div className="p-5 md:p-6 flex flex-wrap items-center justify-between gap-4 border-b border-[var(--ink-border)]"
        style={{ backgroundColor: palette.bg, borderBottomColor: palette.border }}>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 flex items-center gap-1.5">
            <Activity className="h-3.5 w-3.5" style={{ color: palette.fg }} /> Predictive Injury Risk · Risk_v1
          </div>
          <div className="flex items-baseline gap-3 mt-1">
            <span className="font-display font-black text-5xl font-mono-num" style={{ color: palette.fg }} data-testid="injury-risk-score">
              {risk.score}
            </span>
            <span className="text-white/45 text-sm font-mono-num">/ 100</span>
            <span className="ml-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] tracking-[0.25em] uppercase font-bold border"
              style={{ backgroundColor: `${palette.fg}22`, borderColor: palette.border, color: palette.fg }}
              data-testid="injury-risk-band">
              {risk.band}
            </span>
          </div>
        </div>
        <div className="text-xs text-white/45 max-w-xs">
          {risk.inputs?.acute_chronic_ratio != null && (<div>A:C ratio · <span className="text-white font-mono-num">{risk.inputs.acute_chronic_ratio}</span></div>)}
          {risk.inputs?.gird_deg != null && (<div>GIRD · <span className="text-white font-mono-num">{risk.inputs.gird_deg}°</span></div>)}
          <div>{risk.inputs?.logs_examined ?? 0} logs · last {risk.inputs?.window_days ?? 28}d</div>
        </div>
      </div>

      {history.length >= 2 && (
        <div className="px-5 md:px-6 pt-4 pb-2 border-b border-[var(--ink-border)]" data-testid="injury-risk-sparkline">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5" style={{ color: palette.fg }} /> 30-Day trend
            </div>
            {trend && (
              <div className="text-[10px] font-mono-num" data-testid="injury-risk-trend-delta">
                <span className="text-white/45">{trend.first} → {trend.last}</span>
                <span
                  className="ml-2 font-bold"
                  style={{ color: trend.direction === "up" ? "#EF4444" : trend.direction === "down" ? "#10B981" : "#94A3B8" }}
                >
                  {trend.delta > 0 ? "+" : ""}{trend.delta} {trend.direction === "up" ? "↑" : trend.direction === "down" ? "↓" : "→"}
                </span>
              </div>
            )}
          </div>
          <div style={{ height: 60 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <YAxis domain={[0, 100]} hide />
                <RTooltip
                  contentStyle={{ background: "#0A1424", border: `1px solid ${palette.border}`, fontSize: 11 }}
                  itemStyle={{ color: palette.fg }}
                  labelStyle={{ color: "#94A3B8", fontSize: 10 }}
                  formatter={(v) => [`${v}`, "risk"]}
                />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke={palette.fg}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {risk.factors.length > 0 && (
        <div className="p-5 md:p-6 border-b border-[var(--ink-border)]" data-testid="injury-risk-factors">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45">Contributing factors</div>
            <button
              onClick={openInStudio}
              className="text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-fuchsia-400/40 flex items-center gap-1.5"
              data-testid="injury-risk-open-studio"
              title="Snap MotionIQ Studio to this athlete's breakdown frame"
            >
              <Cpu className="h-3 w-3 text-fuchsia-400" /> Open in Studio
            </button>
          </div>
          <ul className="space-y-2">
            {risk.factors.map((f) => (
              <li key={f.key} className="flex items-start justify-between gap-3 text-sm"
                data-testid={`injury-risk-factor-${f.key}`}>
                <span className="flex items-start gap-2 min-w-0">
                  <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: SEVERITY_COLORS[f.severity] || "#94A3B8" }} />
                  <span className="text-white/85 min-w-0">
                    <span className="text-white">{f.signal}</span>
                    <span className="text-white/40 ml-2 text-[10px] uppercase tracking-[0.15em]">{f.module}</span>
                  </span>
                </span>
                <span className="font-mono-num text-xs font-bold shrink-0" style={{ color: SEVERITY_COLORS[f.severity] || "#94A3B8" }}>
                  +{f.delta}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {risk.recommendations.length > 0 && (
        <div className="p-5 md:p-6 border-b border-[var(--ink-border)]" data-testid="injury-risk-recommendations">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mb-3 flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" style={{ color: palette.fg }} /> Recommended actions
          </div>
          <ul className="space-y-2">
            {risk.recommendations.map((r, i) => (
              <li key={i} className="text-sm text-white/80 flex items-start gap-2"
                data-testid={`injury-risk-rec-${i}`}>
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: palette.fg }} />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="p-4 flex items-start gap-2 text-[10px] text-white/40 leading-relaxed">
        <Info className="h-3 w-3 mt-0.5 shrink-0" />
        <span>{risk.disclaimer}</span>
      </div>
    </section>
  );
}
